import React, { useState, useMemo } from "react";
import {
  Flex,
  Box,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  Button,
  useColorModeValue,
  Badge,
  HStack,
  VStack,
  Divider,
  List,
  ListItem,
  ListIcon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import {
  FaMapMarkedAlt,
  FaChartBar,
  FaHistory,
  FaAddressCard,
  FaSitemap,
  FaRegCommentAlt,
  FaDatabase,
  FaUsers,
  FaUserPlus,
  FaKey,
  FaSignInAlt,
  FaCheckCircle,
  FaArrowRight,
  FaUser,
  FaUserShield,
  FaEnvelope,
  FaEdit,
  FaPlus,
  FaShieldAlt,
  FaLayerGroup,
  FaUserCog,
  FaIdCard,
  FaSearch,
  FaTachometerAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { isTokenValid, getRole, isAdmin } from "utils/auth";

const Home = () => {
  const authenticated = isTokenValid();
  const actualRole = authenticated ? (getRole() || "user") : "public";
  const [selectedRole, setSelectedRole] = useState(actualRole);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Active role displayed (Admin can toggle between roles to preview)
  const role = isAdmin() ? selectedRole : actualRole;

  // Theme colors
  const cardBg = useColorModeValue("white", "navy.800");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const calloutBg = useColorModeValue("slate.50", "navy.900");
  const categoryHeaderBg = useColorModeValue("slate.100", "navy.700");
  const inputBg = useColorModeValue("white", "navy.900");

  // Reusable Category Header Component
  const CategoryHeader = ({ title, subtitle, icon, badgeText, badgeColor = "teal", count }) => (
    <Box
      p={{ base: "16px 20px", md: "20px 24px" }}
      bg={categoryHeaderBg}
      borderRadius="18px"
      border="1px solid"
      borderColor={cardBorder}
      mb="20px"
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap="10px">
        <HStack spacing="14px">
          <Box p="10px" borderRadius="12px" bg={`${badgeColor}.500`} color="white">
            <Icon as={icon} boxSize="20px" />
          </Box>
          <Box>
            <HStack spacing="8px">
              <Heading size="md" color={textColor} fontWeight="800">
                {title}
              </Heading>
              <Badge colorScheme={badgeColor} variant="solid" borderRadius="full" px="10px" py="2px" fontSize="2xs">
                {badgeText}
              </Badge>
              {count !== undefined && (
                <Badge colorScheme="gray" variant="subtle" borderRadius="full" px="8px" py="2px" fontSize="2xs">
                  {count} topics
                </Badge>
              )}
            </HStack>
            <Text fontSize="xs" color={subtitleColor} mt="2px">
              {subtitle}
            </Text>
          </Box>
        </HStack>
      </Flex>
    </Box>
  );

  // Filter helper
  const matchesSearch = (textArray) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return textArray.some((t) => t && t.toLowerCase().includes(q));
  };

  // =========================================================================
  // 1. DATA DEFINITIONS FOR EACH CATEGORY (CLEAR, PRACTICAL HOW-TO GUIDES)
  // =========================================================================

  // --- FEATURES MODULES ---
  const allFeatureItems = [
    {
      id: "map",
      title: "Interactive Geographic Map",
      icon: FaMapMarkedAlt,
      color: "teal",
      link: "/txd/mapdata",
      btnText: "Open Interactive Map",
      roles: ["public", "user", "moderator", "admin"],
      summary: "Explore the geographic distribution of reported XP cases across all 24 Tunisian governorates.",
      bullets: [
        "Regional Heatmap: Each governorate is color-shaded based on patient density, highlighting regions with higher prevalence.",
        "Hover & Click Interaction: Move your mouse over or click any governorate to view the exact number of patients and percentage.",
        "Cluster Observations: Identify geographic clusters to assist clinical outreach and genetic counseling.",
        "Map Controls: Use the reset button to return to the full view of Tunisia anytime.",
      ],
      tags: ["map", "geographic", "governorates", "density", "heatmap", "clusters", "tunisia", "regions"],
    },
    {
      id: "stats",
      title: "XP in Numbers (Analytics)",
      icon: FaChartBar,
      color: "blue",
      link: "/txd/stats",
      btnText: "Open Analytics",
      roles: ["public", "user", "moderator", "admin"],
      summary: "Interactive charts detailing diagnostic delays, genetic groups, and demographic patterns.",
      bullets: [
        "Diagnostic Delay Scatter Plot: Compares the age of first symptoms with the age of confirmed clinical diagnosis.",
        "Drag & Zoom: Click and drag over any area of the scatter plot to zoom in on specific age groups, then click reset.",
        "Genetic Groups: View the frequency of complementation groups (such as XP-C, XP-A, and the XP-V variant).",
        "Consanguinity & Demographics: See parental consanguinity rates, sex ratio (male/female), and age distribution charts.",
      ],
      tags: ["stats", "analytics", "scatter plot", "delay", "onset", "diagnosis", "consanguinity", "groups", "zoom", "charts"],
    },
    {
      id: "individuals",
      title: "Individuals (Patient Records)",
      icon: FaAddressCard,
      color: "teal",
      link: "/txd/individuals",
      btnText: "Open Individuals",
      roles: ["user", "moderator", "admin"],
      summary: "Search, filter, and consult clinical dossiers including symptoms, eye and neurological signs, and genetics.",
      bullets: [
        "Search by Code: Type a patient code (e.g. XP100) in the search bar to find a record instantly.",
        "Filter Options: Narrow down records by genetic group (XP-C, XP-A, XP-V), gender, consanguinity, or governorate.",
        "Detailed Dossiers: Click any patient row to open their full file with dermatological lesions, ocular signs, neurological status, and skin cancer history.",
        "Genetic Information: Review assigned complementation groups, gene mutations, and protein consequences.",
        role === "user"
          ? "Read-Only Access: As a collaborator, your access is view-only to safeguard patient data."
          : "Curation Rights: You have privileges to add new patients and edit existing records.",
      ],
      tags: ["individuals", "patients", "dossiers", "search", "filter", "clinical", "skin", "ocular", "genetics", "mutations"],
    },
    {
      id: "families",
      title: "Families & Pedigrees",
      icon: FaSitemap,
      color: "purple",
      link: "/txd/families",
      btnText: "Open Families",
      roles: ["user", "moderator", "admin"],
      summary: "Browse family lineages, consanguinity relationships, and linked affected individuals.",
      bullets: [
        "Family Directory: View family codes, origin governorate, consanguinity degree, and the count of affected members.",
        "Expand Pedigree: Click on any family row to display all associated patients and their diagnostic status.",
        "Hereditary Analysis: Follow autosomal recessive inheritance patterns across branches of related families.",
        role === "user"
          ? "Read-Only View: Inspect family trees and member lists in consultation mode."
          : "Family Management: Create new family profiles and link patient dossiers to pedigrees.",
      ],
      tags: ["families", "pedigree", "lineage", "consanguinity", "hereditary", "tree", "branches", "members"],
    },
    {
      id: "timeline",
      title: "Bibliography Timeline",
      icon: FaHistory,
      color: "cyan",
      link: "/txd/info",
      btnText: "Open Timeline",
      roles: ["public", "user", "moderator", "admin"],
      summary: "A historical research timeline documenting 50 years of XP scientific publications in Tunisia.",
      bullets: [
        "50 Years of Research: Browse 26 chronological milestones spanning five decades of clinical and genetic studies.",
        "Study Summaries: Click any station along the timeline to read author lists, journals, and comprehensive abstracts.",
        "Direct Article Links: Click PubMed or DOI links on any publication to read the full scientific article.",
        "Photoprotection Resources: Access practical guidance on sun protection and specialized UV protective gear.",
      ],
      tags: ["bibliography", "timeline", "literature", "history", "publications", "pubmed", "doi", "articles", "research"],
    },
    {
      id: "forums",
      title: "Scientific Community Forum",
      icon: FaRegCommentAlt,
      color: "orange",
      link: "/txd/forums",
      btnText: "Open Forums",
      roles: ["user", "moderator", "admin"],
      summary: "A peer discussion space for doctors and researchers to discuss clinical cases and care strategies.",
      bullets: [
        "Browse Topics: Explore discussion categories covering clinical cases, UV photoprotection, and genetic sequencing.",
        "Create Discussions: Click '+ New Post' to ask a clinical question or share an observation.",
        "Reply & Collaborate: Comment on active threads to exchange knowledge with fellow specialists.",
        "Confidentiality Rule: Never post patient names or personal contact details. Always use anonymous patient codes.",
      ],
      tags: ["forums", "community", "discussions", "threads", "collaboration", "cases", "clinical questions", "peer"],
    },
    {
      id: "dashboard",
      title: "Dashboard Overview",
      icon: FaTachometerAlt,
      color: "pink",
      link: "/txd/dashboard",
      btnText: "Open Dashboard",
      roles: ["user", "moderator", "admin"],
      summary: "Your home screen with top-level statistics and quick access buttons to key tools.",
      bullets: [
        "Summary Cards: At-a-glance count of total registered patients, documented families, and governorates covered.",
        "Quick Shortcuts: One-click buttons to jump straight to the map, statistics, patient list, or forum.",
        "Recent Activity: See recently updated patient records and active discussions.",
      ],
      tags: ["dashboard", "home", "metrics", "overview", "summary", "quick links"],
    },
  ];

  // --- ACCOUNT MODULES ---
  const allAccountItems = [
    {
      id: "signin",
      title: "Signing In to Your Account",
      icon: FaSignInAlt,
      color: "blue",
      link: "/auth/signin",
      btnText: "Go to Sign In",
      roles: ["public", "user", "moderator", "admin"],
      summary: "How to log in to access your authorized workspace on the platform.",
      bullets: [
        "Access the Login Screen: Click 'Sign In' at the top right of any page.",
        "Enter Credentials: Type your registered email address and your password, then click Sign In.",
        "Automatic Redirect: Once logged in, you will be taken directly to your workspace.",
        "Signing Out: When finished, click the Sign Out icon in the top navigation bar, especially on shared hospital computers.",
      ],
      tags: ["account", "signin", "login", "authentication", "logout", "access"],
    },
    {
      id: "onboarding",
      title: "Activating an Account from an Email Invite",
      icon: FaEnvelope,
      color: "teal",
      roles: ["public", "user", "moderator", "admin"],
      summary: "Step-by-step instructions for completing your registration after receiving an invitation email.",
      bullets: [
        "Check Your Email: When the administration invites you, you will receive an invitation email with an activation link.",
        "Open the Link: Click the link inside the email to open the account registration page.",
        "Fill In Your Details: Enter your First Name, Last Name, Professional Title (e.g. Dermatologist, Geneticist), and Hospital/University.",
        "Set Your Password: Create a secure password of your choice and confirm it.",
        "Instant Activation: Click 'Activate Account' to finalize registration and immediately enter the platform.",
      ],
      tags: ["account", "activation", "onboarding", "invitation", "email", "registration", "signup"],
    },
    {
      id: "password_reset",
      title: "Resetting a Forgotten Password",
      icon: FaKey,
      color: "purple",
      link: "/auth/signin",
      btnText: "Go to Sign In",
      roles: ["user", "moderator", "admin"],
      summary: "How to regain access if you forget your password.",
      bullets: [
        "Click Forgot Password: On the Sign In screen, click the 'Forgot Password' link below the password input.",
        "Enter Your Email: Type the email address linked to your account and submit the form.",
        "Check Your Inbox: An automated password reset email will arrive with a secure link.",
        "Choose a New Password: Click the reset link, enter your new password, and log in right away.",
      ],
      tags: ["account", "password", "reset", "forgot password", "recovery"],
    },
    {
      id: "profile",
      title: "Managing Your Profile & Password",
      icon: FaIdCard,
      color: "blue",
      link: "/txd/profile",
      btnText: "Open Profile",
      roles: ["user", "moderator", "admin"],
      summary: "How to update your professional details and change your password.",
      bullets: [
        "Access Profile: Click 'Profile' in the sidebar or top navigation.",
        "Update Details: Edit your name, clinical department, hospital affiliation, or phone number and click Save.",
        "Change Password: In the password change section, enter your current password, type your new password, and confirm.",
        "View Your Role: Check your assigned role (User, Moderator, or Administrator) displayed at the top of your profile.",
      ],
      tags: ["account", "profile", "credentials", "edit profile", "change password", "settings"],
    },
    {
      id: "request_access",
      title: "Requesting Access (For Medical Professionals)",
      icon: FaEnvelope,
      color: "teal",
      link: "/#contact",
      btnText: "Open Contact Form",
      roles: ["public"],
      summary: "How clinicians and researchers can apply for authorized access to clinical records.",
      bullets: [
        "Who Can Apply: Dermatologists, pediatricians, geneticists, and researchers studying Xeroderma Pigmentosum in Tunisia.",
        "Submit an Inquiry: Go to the Contact section at the bottom of the landing page.",
        "Provide Your Affiliation: Include your full name, clinical title, hospital or university department, and message.",
        "Receive Your Invitation: The platform administrators review requests and email you an official invitation link to create your account.",
      ],
      tags: ["account", "invitation", "request", "collaborate", "contact", "application"],
    },
  ];

  // --- DATA MANAGEMENT MODULES (Moderator & Admin) ---
  const allManagementItems = [
    {
      id: "add_individual",
      title: "Adding a Patient (Step-by-Step)",
      icon: FaPlus,
      color: "teal",
      link: "/txd/individuals",
      btnText: "Go to Individuals",
      roles: ["moderator", "admin"],
      summary: "How to enter a newly diagnosed patient and create their clinical dossier.",
      bullets: [
        "Step 1 - Open the Form: Navigate to Individuals from the left sidebar and click the '+ Add Individual' button.",
        "Step 2 - Demographics: Enter the patient code (e.g. XP105), birth date, gender, origin governorate, parental consanguinity, and family code.",
        "Step 3 - Onset & Diagnosis: Record the age when sun sensitivity symptoms first appeared, and the age of clinical diagnosis.",
        "Step 4 - Symptoms: Check off all observed signs (freckling, dry skin, photophobia, corneal opacity, hearing loss, skin tumors).",
        "Step 5 - Genetics: Select the complementation group (XP-C, XP-A, XP-V, etc.), gene name, and mutation details if known.",
        "Step 6 - Save: Click 'Save Record'. The patient profile is immediately published in the database.",
      ],
      tags: ["management", "add", "new patient", "individual", "create", "symptoms", "genetics"],
    },
    {
      id: "edit_individual",
      title: "Updating Records & Clinical Follow-ups",
      icon: FaEdit,
      color: "blue",
      link: "/txd/individuals",
      btnText: "View Patient List",
      roles: ["moderator", "admin"],
      summary: "How to update a patient's file after follow-up visits or new genetic sequencing.",
      bullets: [
        "Find the Patient: Locate the patient in the Individuals list and click on their row or the 'Edit' button.",
        "Follow-Up Updates: Add newly documented skin lesions, surgical interventions, or visual checkup results.",
        "Genotype Confirmation: Update preliminary clinical complementation groups once DNA sequencing is confirmed.",
        "Family Links: Reassign or link the patient to a newly identified family tree when kinship is confirmed.",
        "Save Updates: Click 'Save Changes' to update the record.",
      ],
      tags: ["management", "edit", "update", "followup", "modify", "notes"],
    },
    {
      id: "manage_families",
      title: "Creating & Managing Families",
      icon: FaSitemap,
      color: "purple",
      link: "/txd/families",
      btnText: "Manage Families",
      roles: ["moderator", "admin"],
      summary: "How to create family records and link related patients together.",
      bullets: [
        "Create a Family: Go to Families -> Click '+ Add Family'. Enter a family code (e.g. FAM22), governorate, and consanguinity tier.",
        "Link Patients: When adding or editing an individual, select this family code to automatically attach them to the family pedigree.",
        "Review Pedigree: Open any family to verify that all siblings and relatives appear correctly.",
      ],
      tags: ["management", "families", "pedigree", "create family", "consanguinity", "tree"],
    },
    {
      id: "invite_colleagues",
      title: "Inviting Medical Colleagues",
      icon: FaUserPlus,
      color: "teal",
      link: "/txd/invitations",
      btnText: "Manage Invitations",
      roles: ["moderator", "admin"],
      summary: "How to send an official platform invitation to a fellow clinician or researcher.",
      bullets: [
        "Open Invitations: Navigate to Invitations in the sidebar.",
        "Enter Colleague's Email: Type your colleague's professional email address into the invite form.",
        "Select Role: Moderators can invite Collaborators (Users); Administrators can invite Users or Moderators.",
        "Send Invite: Click 'Send Invitation'. The platform will automatically email them an invitation link with setup instructions.",
      ],
      tags: ["management", "invitations", "invite", "colleague", "send invite"],
    },
  ];

  // --- ADMINISTRATION MODULES (Admin Only) ---
  const allAdminItems = [
    {
      id: "user_management",
      title: "User Accounts & Role Permissions",
      icon: FaUsers,
      color: "purple",
      link: "/txd/users",
      btnText: "Open Users",
      roles: ["admin"],
      summary: "Manage registered accounts and assign roles (User, Moderator, Admin).",
      bullets: [
        "View All Users: See every registered user along with their full name, email, current role, and hospital affiliation.",
        "Change Roles: Click the role selector next to any user to promote or demote them between User, Moderator, and Admin.",
        "Account Safety: Administrators cannot accidentally delete or demote their own account to prevent lockout.",
        "Moderator Protection: Moderators cannot alter or remove Administrator accounts.",
      ],
      tags: ["administration", "users", "roles", "permissions", "promote", "demote", "security"],
    },
    {
      id: "invitation_governance",
      title: "Invitation Monitoring & Management",
      icon: FaUserPlus,
      color: "blue",
      link: "/txd/invitations",
      btnText: "Open Invitations",
      roles: ["admin"],
      summary: "Track sent invitations, resend links, or cancel pending invitations.",
      bullets: [
        "Status Table: View all invitations categorized by Pending, Accepted, or Expired.",
        "Resend Link: Click 'Resend' if a colleague misplaced their invitation email or if it expired.",
        "Revoke Invitation: Click 'Revoke' to cancel an invitation sent by mistake before it is accepted.",
      ],
      tags: ["administration", "invitations", "resend", "revoke", "status"],
    },
    {
      id: "audit_logs",
      title: "System Audit Logs (Change History)",
      icon: FaHistory,
      color: "teal",
      link: "/txd/logs",
      btnText: "Open Logs",
      roles: ["admin"],
      summary: "Review who created, modified, or deleted records with exact before/after details.",
      bullets: [
        "Filter by Activity: Filter log entries by action (CREATE, UPDATE, DELETE), section (Individuals, Families, Users), or user email.",
        "Date Range: Search for activities that occurred during a specific week or month.",
        "Inspect Changes: Click any log entry to see exactly which fields were changed and what the previous values were.",
        "Export to CSV: Click 'Export CSV' to download a spreadsheet copy of the activity history for institutional reports.",
      ],
      tags: ["administration", "audit", "logs", "history", "changes", "export", "csv"],
    },
    {
      id: "backup_restore",
      title: "Database Backup & Restore",
      icon: FaDatabase,
      color: "purple",
      link: "/txd/backup",
      btnText: "Open Backup",
      roles: ["admin"],
      summary: "Download database backups to your computer and restore data safely.",
      bullets: [
        "Health Overview: Check database status and table counts at a glance.",
        "One-Click Backup: Click 'Download Backup' to immediately save a complete backup file to your computer.",
        "Safe Restore: To restore, upload a previously saved backup file and click 'Restore Database'.",
        "Account Safety: The restore process safely protects all active user accounts so no one gets locked out.",
      ],
      tags: ["administration", "backup", "restore", "download backup", "disaster recovery"],
    },
  ];

  // Filter items by role and search query
  const featureItems = useMemo(
    () =>
      allFeatureItems
        .filter((item) => item.roles.includes(role))
        .filter((item) =>
          matchesSearch([item.title, item.summary, ...item.bullets, ...item.tags])
        ),
    [role, searchQuery]
  );

  const accountItems = useMemo(
    () =>
      allAccountItems
        .filter((item) => item.roles.includes(role))
        .filter((item) =>
          matchesSearch([item.title, item.summary, ...item.bullets, ...item.tags])
        ),
    [role, searchQuery]
  );

  const managementItems = useMemo(
    () =>
      allManagementItems
        .filter((item) => item.roles.includes(role))
        .filter((item) =>
          matchesSearch([item.title, item.summary, ...item.bullets, ...item.tags])
        ),
    [role, searchQuery]
  );

  const adminItems = useMemo(
    () =>
      allAdminItems
        .filter((item) => item.roles.includes(role))
        .filter((item) =>
          matchesSearch([item.title, item.summary, ...item.bullets, ...item.tags])
        ),
    [role, searchQuery]
  );

  // Helper to render a clean, readable card
  const renderCard = (item) => (
    <Box
      key={item.id}
      bg={cardBg}
      p="24px"
      borderRadius="20px"
      border="1px solid"
      borderColor={cardBorder}
      boxShadow="0 4px 16px rgba(0,0,0,0.03)"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      transition="all 0.2s"
      _hover={{ transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}
    >
      <Box>
        <Flex justify="space-between" align="center" mb="14px">
          <HStack spacing="12px">
            <Box
              p="10px"
              borderRadius="12px"
              bg={`${item.color}.50`}
              color={`${item.color}.600`}
              _dark={{ bg: "whiteAlpha.100", color: `${item.color}.300` }}
            >
              <Icon as={item.icon} boxSize="20px" />
            </Box>
            <Heading size="sm" color={textColor} fontWeight="700">
              {item.title}
            </Heading>
          </HStack>
        </Flex>

        <Text fontSize="xs" color={subtitleColor} lineHeight="tall" mb="14px">
          {item.summary}
        </Text>

        <List spacing={2} fontSize="2xs" color={subtitleColor} mb="18px">
          {item.bullets.map((bullet, bIdx) => (
            <ListItem key={bIdx} display="flex" alignItems="flex-start">
              <ListIcon as={FaCheckCircle} color={`${item.color}.500`} mt="2px" />
              <span>{bullet}</span>
            </ListItem>
          ))}
        </List>
      </Box>

      {item.link && (
        <Box pt="12px" borderTop="1px solid" borderColor={cardBorder}>
          <Button
            as={Link}
            to={item.link}
            size="xs"
            colorScheme={item.color}
            variant="outline"
            rightIcon={<FaArrowRight />}
            w="full"
          >
            {item.btnText || "Open Tool"}
          </Button>
        </Box>
      )}
    </Box>
  );

  // Available categories for the active role
  const categories = useMemo(() => {
    const list = [
      { id: "all", label: "All Categories", count: featureItems.length + accountItems.length + managementItems.length + adminItems.length },
      { id: "features", label: "Features", count: featureItems.length, icon: FaLayerGroup, color: "teal" },
      { id: "account", label: "Account", count: accountItems.length, icon: FaUserCog, color: "blue" },
    ];
    if (["moderator", "admin"].includes(role)) {
      list.push({
        id: "management",
        label: "Data Management",
        count: managementItems.length,
        icon: FaEdit,
        color: "orange",
      });
    }
    if (role === "admin") {
      list.push({
        id: "administration",
        label: "Administration",
        count: adminItems.length,
        icon: FaShieldAlt,
        color: "purple",
      });
    }
    return list;
  }, [role, featureItems.length, accountItems.length, managementItems.length, adminItems.length]);

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} pb="60px" px={{ base: "16px", md: "36px" }}>
      <Box maxW="1180px" mx="auto" w="100%">
        {/* Administrator Tier Preview Switcher */}
        {isAdmin() && (
          <Box
            mb="24px"
            p="14px 20px"
            bg={calloutBg}
            borderRadius="16px"
            border="1px solid"
            borderColor={cardBorder}
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
              <HStack spacing="8px">
                <Icon as={FaKey} color="purple.500" />
                <Text fontSize="xs" fontWeight="700" color={textColor}>
                  Administrator Tier Preview:
                </Text>
                <Text fontSize="xs" color={subtitleColor}>
                  (Switch to preview how the guide looks for other roles)
                </Text>
              </HStack>
              <HStack spacing="6px">
                {["admin", "moderator", "user", "public"].map((r) => (
                  <Button
                    key={r}
                    size="xs"
                    colorScheme={selectedRole === r ? "purple" : "gray"}
                    variant={selectedRole === r ? "solid" : "outline"}
                    borderRadius="8px"
                    textTransform="capitalize"
                    onClick={() => {
                      setSelectedRole(r);
                      setActiveTab("all");
                    }}
                  >
                    {r}
                  </Button>
                ))}
              </HStack>
            </Flex>
          </Box>
        )}

        {/* Hero Header Banner */}
        <Box textAlign="center" maxW="760px" mx="auto" mb="28px">
          <HStack justify="center" spacing="8px" mb="10px">
            <Badge
              colorScheme={
                role === "admin"
                  ? "purple"
                  : role === "moderator"
                  ? "blue"
                  : role === "user"
                  ? "teal"
                  : "gray"
              }
              variant="solid"
              px="12px"
              py="4px"
              borderRadius="full"
              fontSize="xs"
              fontWeight="700"
              textTransform="capitalize"
            >
              {role === "public" ? "Public User Guide" : `${role} User Guide`}
            </Badge>
            <Badge colorScheme="gray" variant="subtle" px="10px" py="4px" borderRadius="full" fontSize="xs">
              Tunisian XP Database
            </Badge>
          </HStack>
          <Heading size="xl" color={textColor} fontWeight="800" mb="8px">
            Platform User Guide
          </Heading>
          <Text fontSize="md" color={subtitleColor} lineHeight="tall">
            Step-by-step instructions on how to use every feature and manage your account.
          </Text>
        </Box>

        {/* Search and Category Filter Tabs */}
        <Box mb="32px">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            gap="16px"
            mb="16px"
          >
            {/* Category Navigation Pills */}
            <HStack spacing="8px" wrap="wrap">
              {categories.map((cat) => {
                const isActive = activeTab === cat.id;
                const scheme = cat.color || (isActive ? "teal" : "gray");
                return (
                  <Button
                    key={cat.id}
                    size="sm"
                    variant={isActive ? "solid" : "outline"}
                    colorScheme={scheme}
                    borderRadius="full"
                    px="16px"
                    py="8px"
                    fontWeight={isActive ? "700" : "500"}
                    boxShadow={isActive ? "0 4px 12px rgba(0,0,0,0.14)" : "none"}
                    borderColor={isActive ? `${scheme}.500` : cardBorder}
                    leftIcon={cat.icon ? <Icon as={cat.icon} /> : undefined}
                    onClick={() => setActiveTab(cat.id)}
                    _hover={{
                      transform: "translateY(-1px)",
                    }}
                  >
                    <span>{cat.label}</span>
                    <Badge
                      ml="8px"
                      borderRadius="full"
                      px="8px"
                      py="1px"
                      colorScheme={isActive ? "whiteAlpha" : scheme}
                      variant="solid"
                      fontSize="2xs"
                    >
                      {cat.count}
                    </Badge>
                  </Button>
                );
              })}
            </HStack>

            {/* Real-time Guide Search */}
            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color={subtitleColor} />
              </InputLeftElement>
              <Input
                placeholder="Search guide (e.g. map, delay, password)..."
                size="sm"
                borderRadius="full"
                bg={inputBg}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Flex>
        </Box>

        {/* CATEGORY: FEATURES */}
        {(activeTab === "all" || activeTab === "features") && featureItems.length > 0 && (
          <Box mb="40px" id="category-features">
            <CategoryHeader
              title="Features"
              subtitle="How to use the map, statistics, patient files, bibliography timeline, and forum"
              icon={FaLayerGroup}
              badgeText="Platform Tools"
              badgeColor="teal"
              count={featureItems.length}
            />
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="20px">
              {featureItems.map(renderCard)}
            </SimpleGrid>
          </Box>
        )}

        {/* CATEGORY: ACCOUNT */}
        {(activeTab === "all" || activeTab === "account") && accountItems.length > 0 && (
          <Box mb="40px" id="category-account">
            <CategoryHeader
              title="Account"
              subtitle="How to log in, activate an invite, reset your password, or update your profile"
              icon={FaUserCog}
              badgeText="Account Management"
              badgeColor="blue"
              count={accountItems.length}
            />
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="20px">
              {accountItems.map(renderCard)}
            </SimpleGrid>
          </Box>
        )}

        {/* CATEGORY: DATA MANAGEMENT (Moderator & Admin) */}
        {["moderator", "admin"].includes(role) &&
          (activeTab === "all" || activeTab === "management") &&
          managementItems.length > 0 && (
            <Box mb="40px" id="category-management">
              <CategoryHeader
                title="Data Management"
                subtitle="How to add new patients, update follow-up notes, create families, and invite colleagues"
                icon={FaEdit}
                badgeText="Curation Tools"
                badgeColor="purple"
                count={managementItems.length}
              />
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing="20px">
                {managementItems.map(renderCard)}
              </SimpleGrid>
            </Box>
          )}

        {/* CATEGORY: ADMINISTRATION (Admin Only) */}
        {role === "admin" &&
          (activeTab === "all" || activeTab === "administration") &&
          adminItems.length > 0 && (
            <Box mb="40px" id="category-administration">
              <CategoryHeader
                title="Administration"
                subtitle="How to manage user accounts, review audit logs, and download database backups"
                icon={FaShieldAlt}
                badgeText="Admin Controls"
                badgeColor="purple"
                count={adminItems.length}
              />
              <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} spacing="20px">
                {adminItems.map(renderCard)}
              </SimpleGrid>
            </Box>
          )}

        {/* No Results Fallback */}
        {featureItems.length === 0 &&
          accountItems.length === 0 &&
          managementItems.length === 0 &&
          adminItems.length === 0 && (
            <Box textAlign="center" py="60px" bg={cardBg} borderRadius="20px" border="1px solid" borderColor={cardBorder}>
              <Icon as={FaSearch} boxSize="36px" color={subtitleColor} mb="12px" />
              <Heading size="md" color={textColor} mb="6px">
                No matching guide topics found
              </Heading>
              <Text fontSize="sm" color={subtitleColor} mb="16px">
                No articles matched your search query "{searchQuery}". Try searching for terms like "map", "delay", "password", or "patient".
              </Text>
              <Button size="sm" colorScheme="teal" onClick={() => setSearchQuery("")}>
                Clear Search
              </Button>
            </Box>
          )}
      </Box>
    </Flex>
  );
};

export default Home;